import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Dialog,
  TextField,
  Typography,
  CircularProgress,
  SelectChangeEvent,
  Button,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  useUpdateOrderNumberMutation,
  useGetCurrentOrderNumberQuery,
  useGetSequenceFormOptionsQuery
} from '@/services/orders.api';
import { FilterOptions } from '@/types/orders.types';
import { useSnackbar } from '@/hooks';

import { defaultOrderFilters, getNextDialogFilters } from '../utils';
import { OrderNumberDialogSelect } from '../OrderNumberDialogSelect';

import { Filters, OrderNumberDialogProps } from './OrderNumberDialog.types';

import './OrderNumberDialog.css';

/**
 * Dialog for changing order number for a specific division and legal entity
 * Allows users to select division, legal entity, view current number, and set new number
 * Includes validation and error handling

 */
export function OrderNumberDialog({ open, onClose, onHistoryRefetch }: OrderNumberDialogProps): JSX.Element {
  const { setSnackbarMessage } = useSnackbar();

  const [newNumber, setNewNumber] = useState<string>('');
  const [filters, setFilters] = useState<Filters>(defaultOrderFilters);

  const { data: filterOptionsData, isLoading: filterOptionsLoading } = useGetSequenceFormOptionsQuery(filters.divisionId, {
    skip: !open,
    refetchOnMountOrArgChange: true
  });

  const { data: currentOrderNumberData, isLoading: currentOrderNumberLoading } = useGetCurrentOrderNumberQuery(
    { divisionId: filters.divisionId!, legalEntityId: filters.legalEntityId! },
    {
      skip: !open || !filters.divisionId || !filters.legalEntityId,
      refetchOnMountOrArgChange: true
    }
  );

  const [updateOrderNumber, { isLoading: savingNewNumber }] = useUpdateOrderNumberMutation();

  const currentOrderNumber = useMemo(() => {
    if (currentOrderNumberData && filters.divisionId && filters.legalEntityId) {
      return currentOrderNumberData.current_number;
    }
    return undefined;
  }, [currentOrderNumberData, filters.divisionId, filters.legalEntityId]);

  const filterOptions = useMemo<FilterOptions>(
    () => filterOptionsData ?? { divisions: [], legal_entities: [] },
    [filterOptionsData]
  );

  useEffect(() => {
    if (!open) {
      setFilters({ divisionId: undefined, legalEntityId: undefined });
      setNewNumber('');
    }
  }, [open]);

  const handleChangeFilters = (prop: 'divisionId' | 'legalEntityId') => (e: SelectChangeEvent<number | string>) => {
    const nextFilters = getNextDialogFilters(filters, prop, e.target.value);
    setFilters(nextFilters);
  };

  const handleSave = async () => {
    if (!filters.divisionId || !filters.legalEntityId || !newNumber) {
      setSnackbarMessage('Будь ласка, заповніть всі поля', 'error');
      onHistoryRefetch();
      return;
    }

    const numberValue = parseInt(newNumber);
    if (isNaN(numberValue) || numberValue <= 0) {
      setSnackbarMessage('Порядковий номер повинен бути додатним числом', 'error');
      onHistoryRefetch();
      return;
    }

    try {
      await updateOrderNumber({
        division_id: filters.divisionId,
        legal_entity_id: filters.legalEntityId,
        new_number: numberValue
      }).unwrap();
      setSnackbarMessage('Порядковий номер успішно оновлено', 'success');
      onClose();
    } catch (error: any) {
      const errorMessage = error.data?.error || error.message || 'Помилка оновлення порядкового номеру';
      setSnackbarMessage(errorMessage, 'error');
    }
  };

  const handleClose = () => {
    if (!savingNewNumber) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: 'order-number-dialog-paper'
      }}
    >
      <Box className="order-number-dialog-header">
        <Typography className="order-number-dialog-title">Зміна порядкового номеру</Typography>
        <IconButton onClick={handleClose} disabled={savingNewNumber} className="order-number-dialog-close-btn">
          <Close />
        </IconButton>
      </Box>

      <Box className="order-number-dialog-content">
        {filterOptionsLoading ? (
          <Box className="order-number-dialog-loading">
            <CircularProgress size={32} className="order-number-dialog-loading-spinner" />
            <Typography sx={{ fontSize: '0.9rem' }}>Завантаження даних...</Typography>
          </Box>
        ) : (
          <>
            <OrderNumberDialogSelect
              label="Дивізіон"
              value={filters.divisionId ?? ''}
              options={filterOptions.divisions}
              placeholder="Оберіть дивізіон"
              disabled={savingNewNumber}
              onChange={handleChangeFilters('divisionId')}
            />

            <OrderNumberDialogSelect
              label="Юридична особа"
              value={filters.legalEntityId ?? ''}
              options={filterOptions.legal_entities}
              placeholder="Оберіть юридичну особу"
              disabled={savingNewNumber || !filters.divisionId}
              onChange={handleChangeFilters('legalEntityId')}
            />

            <Box className="order-number-dialog-grid">
              <Box className="order-number-dialog-field-group">
                <Typography className="order-number-dialog-field-label">Поточний номер</Typography>
                <Box className="order-number-dialog-current-number">
                  {currentOrderNumberLoading ? (
                    <CircularProgress size={16} sx={{ color: 'rgb(132, 189, 0)' }} />
                  ) : (
                    <Typography
                      className={
                        currentOrderNumber !== undefined
                          ? 'order-number-dialog-current-number-value'
                          : 'order-number-dialog-current-number-empty'
                      }
                    >
                      {currentOrderNumber ?? '-'}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box className="order-number-dialog-field-group">
                <Typography className="order-number-dialog-field-label">Новий номер</Typography>
                <TextField
                  value={newNumber}
                  onChange={e => setNewNumber(e.target.value)}
                  size="small"
                  type="number"
                  placeholder={filters.divisionId && filters.legalEntityId ? 'Введіть новий номер' : '-'}
                  disabled={savingNewNumber || !filters.divisionId || !filters.legalEntityId}
                  inputProps={{ min: 1 }}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>

      {!filterOptionsLoading && (
        <Box className="order-number-dialog-actions">
          <Button onClick={handleClose} disabled={savingNewNumber} className="order-number-dialog-cancel-btn">
            Скасувати
          </Button>
          <Button
            className="order-number-dialog-save-btn"
            disabled={savingNewNumber || !filters.divisionId || !filters.legalEntityId || !newNumber}
            onClick={handleSave}
          >
            {savingNewNumber ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Зберегти'}
          </Button>
        </Box>
      )}
    </Dialog>
  );
}
